import { cn } from "@/lib/utils";

// Step icons
function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  );
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
    </svg>
  );
}

function BoltIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

function PenIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

interface Step {
  number: number;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
}

const STEPS: Step[] = [
  { number: 1, title: "Отримувач", icon: UserIcon },
  { number: 2, title: "Будинок", icon: BuildingIcon },
  { number: 3, title: "Обладнання", icon: BoltIcon },
  { number: 4, title: "Документи", icon: DocumentIcon },
  { number: 5, title: "Підписання", icon: PenIcon },
];

interface FormStepperProps {
  currentStep: number;
  onStepClick?: (step: number) => void;
  completedSteps?: number[];
}

export function FormStepper({ currentStep, onStepClick, completedSteps = [] }: FormStepperProps) {
  const activeStep = STEPS[currentStep - 1];

  return (
    <div className="w-full">
      {/* Desktop stepper */}
      <div className="hidden md:block">
        <div className="flex items-center justify-center">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.number === currentStep;
            const isCompleted = completedSteps.includes(step.number) || step.number < currentStep;
            const isPassed = step.number <= currentStep;
            const isClickable = onStepClick && (isCompleted || step.number <= currentStep);

            return (
              <div key={step.number} className="flex items-center">
                {/* Step with icon */}
                <button
                  type="button"
                  onClick={() => isClickable && onStepClick?.(step.number)}
                  disabled={!isClickable}
                  className={cn(
                    "flex flex-col items-center gap-1.5 transition-all",
                    isClickable ? "cursor-pointer" : "cursor-default"
                  )}
                >
                  <div className="relative">
                    {/* Main circle with icon */}
                    <div
                      className={cn(
                        "w-12 h-12 rounded-2xl flex items-center justify-center transition-all",
                        isActive && "bg-primary text-primary-foreground shadow-lg shadow-primary/30",
                        isCompleted && !isActive && "bg-green-500 text-white",
                        !isActive && !isCompleted && "bg-muted text-muted-foreground"
                      )}
                    >
                      {isCompleted && !isActive ? (
                        <CheckIcon className="w-6 h-6" />
                      ) : (
                        <Icon className="w-6 h-6" />
                      )}
                    </div>
                    
                    {/* Small number badge */}
                    <span
                      className={cn(
                        "absolute -top-1 -right-1 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center",
                        isActive && "bg-white text-primary shadow-sm",
                        isCompleted && !isActive && "bg-white text-green-600 shadow-sm",
                        !isActive && !isCompleted && "bg-background text-muted-foreground border-2 border-muted"
                      )}
                    >
                      {step.number}
                    </span>
                  </div>
                  
                  {/* Step title */}
                  <span 
                    className={cn(
                      "text-[11px] font-medium transition-all",
                      isActive && "text-primary",
                      isCompleted && !isActive && "text-green-600",
                      !isPassed && "text-muted-foreground"
                    )}
                  >
                    {step.title}
                  </span>
                </button>

                {/* Connector line */}
                {index < STEPS.length - 1 && (
                  <div
                    className={cn(
                      "w-10 lg:w-16 h-0.5 mx-2 rounded-full transition-all",
                      step.number < currentStep ? "bg-green-500" : "bg-muted"
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile stepper */}
      <div className="md:hidden">
        {/* Progress bar */}
        <div className="flex gap-1.5 mb-3">
          {STEPS.map((step) => (
            <div
              key={step.number}
              className={cn(
                "h-1.5 flex-1 rounded-full transition-all",
                step.number < currentStep && "bg-green-500",
                step.number === currentStep && "bg-primary",
                step.number > currentStep && "bg-muted"
              )}
            />
          ))}
        </div>
        
        {/* Current step info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
              {activeStep && <activeStep.icon className="w-4 h-4" />}
            </span>
            <div>
              <span className="text-[13px] font-medium">{activeStep?.title}</span>
              <span className="text-[11px] text-muted-foreground ml-2">Крок {currentStep}</span>
            </div>
          </div>
          <span className="text-[12px] text-muted-foreground">
            {currentStep} / {STEPS.length}
          </span>
        </div>
      </div>
    </div>
  );
}
